import { useRef, useContext, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { MediaOptions } from '@atlaskit/editor-plugin-media/types';
import type { UploadRejectionData } from '@atlaskit/media-picker/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { mediaPlugin } from '@atlaskit/editor-plugin-media/media-plugin';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import { fg } from '@atlaskit/platform-feature-flags';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAttachmentSizeLimitErrorFlag } from '@confluence/fabric-media-support/entry-points/attachmentSizeLimitErrorHelpers.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	createEditorMediaProvider,
	createRendererMediaProvider,
	createEditorMediaProviderDynamic,
	createRendererMediaProviderDynamic,
} from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import { getMediaOptions } from '../utils/mediaOptions.ts';
import { expValEquals } from '@confluence/feature-experiments';

export const useMediaOptions = ({
	contentId,
	contentType,
	editorAPI,
	flags,
	viewMode,
	mediaViewerExtensions,
	fallbackMediaNameFetcher,
}: {
	contentId: string;
	contentType: string;
	editorAPI: PublicPluginAPI<[typeof mediaPlugin]> | undefined;
	flags?: FlagsStateContainer;
	viewMode?: boolean;
	mediaViewerExtensions?: MediaViewerExtensions;
	fallbackMediaNameFetcher?: (id: string) => Promise<string>;
}): MediaOptions => {
	const intl = useIntl();
	const { featureFlags } = useSessionData();
	const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceKey = useSpaceKey();
	const ssrMediaConfig = useMediaSSRModeAndAuthConfig({
		contentId,
		spaceKey: spaceKey ?? undefined,
		isExternalShare,
	});

	const resolver = useRef<(provider: any) => void>();
	// Store a stable Promise that will be resolved once when mediaProvider is created
	// This avoids race conditions when ssrMediaProvider changes after the effect has run
	const mediaProviderPromiseRef = useRef<Promise<any>>();
	if (
		!mediaProviderPromiseRef.current &&
		expValEquals('platform_editor_media_vc_fixes', 'isEnabled', true)
	) {
		mediaProviderPromiseRef.current = new Promise((resolve) => {
			resolver.current = resolve;
		});
	}
	// Keep most recent option values so dynamic provider getters don't close over stale ones
	const latestParamsRef = useRef({ contentId, spaceKey, isExternalShare, contentType, viewMode });
	latestParamsRef.current = { contentId, spaceKey, isExternalShare, contentType, viewMode };

	// Holds the most recently resolved MediaProvider so we can call refreshUploadDataIfNeeded
	// on it when contentId changes (see useEffect below). This closes the window where the
	// old provider's uploadParams.collection is stale after a SPA navigation but before the
	// new provider Promise resolves.
	const resolvedProviderRef = useRef<
		(MediaProvider & { refreshUploadDataIfNeeded?: () => Promise<void> }) | undefined
	>(undefined);
	const showAttachmentLimitReachedFlag = useCallback(
		(data: UploadRejectionData) => {
			if (flags && data) {
				void flags.showErrorFlag(getAttachmentSizeLimitErrorFlag(data, intl)).then(() => {
					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							name: 'nextEditorAttachmentTooLargeFlag',
						},
					}).fire();
				});
			}
			return !!data;
		},
		[flags, intl, createAnalyticsEvent],
	);

	useEffect(() => {
		if (!contentId) {
			return;
		}

		const createMediaProvider = () => {
			// Skip creating media provider in SSR environment
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				return;
			}

			if (viewMode) {
				return fg('confluence_frontend_transition_preloading_system')
					? createRendererMediaProviderDynamic(() => ({
							contentId: latestParamsRef.current.contentId,
							spaceKey: latestParamsRef.current.spaceKey,
							isExternalShare: latestParamsRef.current.isExternalShare,
						}))
					: createRendererMediaProvider({ contentId, spaceKey, isExternalShare });
			}
			if (contentType) {
				return fg('confluence_frontend_transition_preloading_system')
					? createEditorMediaProviderDynamic(() => ({
							contentId: latestParamsRef.current.contentId,
							spaceKey: latestParamsRef.current.spaceKey,
							contentType: latestParamsRef.current.contentType!,
							isExternalShare: latestParamsRef.current.isExternalShare,
							// Passing undefined will allow us to use the default atlaskit size limit error behavior
							onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
						}))
					: createEditorMediaProvider({
							contentId,
							spaceKey,
							contentType,
							isExternalShare,
							// Passing undefined will allow us to use the default atlaskit size limit error behavior
							onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
						});
			}
		};

		// When the fix is enabled and we already have a resolved provider (i.e. this is a
		// subsequent contentId change / SPA navigation), reuse the existing provider instance
		// rather than creating a new one concurrently. The provider's getOptions() getter
		// already returns the new contentId via latestParamsRef, so auth tokens and view config
		// are dynamic. We just need to refresh upload data and re-notify the media plugin.
		if (
			fg('confluence_frontend_fix_stale_media_collection') &&
			fg('confluence_frontend_transition_preloading_system') &&
			resolvedProviderRef.current
		) {
			const currentProvider = resolvedProviderRef.current;
			// Await the refresh before setProvider so the media plugin reads the correct
			// collection when it initialises — not the stale value from the previous page.
			const refreshed = currentProvider.refreshUploadDataIfNeeded?.().then(() => {
				editorAPI?.media?.actions.setProvider(Promise.resolve(currentProvider));
			});
			void refreshed;
			return;
		}

		const mediaProvider = createMediaProvider();
		if (mediaProvider) {
			editorAPI?.media?.actions.setProvider(mediaProvider);

			if (resolver.current) {
				resolver.current(mediaProvider);

				if (fg('confluence_frontend_fix_stale_media_collection')) {
					// Clear resolver so Strict Mode's second invocation doesn't call it again,
					// and track the provider instance the bridge holds so refreshUploadDataIfNeeded
					// is called on the same instance on SPA navigation — not a duplicate.
					resolver.current = undefined;
					void Promise.resolve(mediaProvider).then((resolved) => {
						resolvedProviderRef.current = resolved as MediaProvider & {
							refreshUploadDataIfNeeded?: () => Promise<void>;
						};
					});
				}
			}
		}
	}, [
		contentId,
		contentType,
		spaceKey,
		isExternalShare,
		viewMode,
		flags,
		showAttachmentLimitReachedFlag,
		featureFlags,
		editorAPI,
	]);

	const ssrMediaProvider = useMemo(() => {
		if (expValEquals('platform_editor_media_vc_fixes', 'isEnabled', true)) {
			if (!ssrMediaConfig) {
				return undefined;
			}
			return {
				viewMediaClientConfig: ssrMediaConfig.config,
			} as MediaProvider;
		}
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!ssrMediaConfig || !process.env.REACT_SSR) {
			return undefined;
		}
		return {
			viewMediaClientConfig: ssrMediaConfig.config,
		} as MediaProvider;
	}, [ssrMediaConfig]);

	// IMPORTANT: If you change the code below, please also update the
	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/mediaPluginOptions/mediaPluginOptions.ts
	return useMemo(
		() =>
			getMediaOptions({
				mediaProvider: expValEquals('platform_editor_media_vc_fixes', 'isEnabled', true)
					? mediaProviderPromiseRef.current!
					: new Promise((resolve) => (resolver.current = resolve)),
				ssrMediaProvider,
				intl,
				featureFlags,
				mediaViewerExtensions,
				fallbackMediaNameFetcher,
			}),
		[intl, featureFlags, ssrMediaProvider, mediaViewerExtensions, fallbackMediaNameFetcher],
	);
};
